import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Wating from "../../assets/images/waiting.png";
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { useLanguageNavigate } from "../../hooks/useLanguageNavigation";
import { getAddressesByUserId } from "../../redux/reducer/userAddressReducer";
import { addToCart, fetchMyCarts } from "../../redux/reducer/cartSlice";
import {
  createProductItem,
  getProductItemById,
} from "../../redux/reducer/productReducer";
import { toast } from "react-toastify";

const GalbElHabebPopup = forwardRef(({ onClose, result, func }, ref) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [productLink, setProductLink] = useState(result?.link || "");
  const [additionalInformation, setAdditionalInformation] = useState("");
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [sizeError, setSizeError] = useState(false);
  const [colorError, setColorError] = useState(false);
  const [additionalInformationError, setAdditionalInformationError] =
    useState(false);
  const [sizealert, setsizealert] = useState(false);

  const dispatch = useDispatch();
  const [isDisabled, setIsDisabled] = useState(false);
  const buttonRef = useRef(null);
  const direction = localStorage.getItem("language") === "ar" ? "rtl" : "ltr";
  // console.log(direction);
  const { carts } = useSelector((state) => state.carts);
  const navigateWithLang = useLanguageNavigate();

  useEffect(() => {
    if (func) {
      dispatch(fetchMyCarts());
      dispatch(getAddressesByUserId());
    }
  }, [dispatch, func]);

  const findCarts = carts.filter(
    (cart) => cart.status === "pending" || cart.status === "priced"
  );
  const handleAddToCart = async () => {
    if (!size.trim()) {
      setSizeError(true);
      toast.error(t("PleaseEnterSize"), { autoClose: 2000 });
      document.getElementById("size")?.focus();
      return;
    } else {
      setSizeError(false);
    }

    // ✅ Validation: تأكد من إدخال الـ Additional Notes
    if (!additionalInformation.trim()) {
      setAdditionalInformationError(true);
      toast.error(t("PleaseEnterAdditionalNotes"), { autoClose: 2000 });
      document.getElementById("additionalInfo")?.focus();
      return;
    } else {
      setAdditionalInformationError(false);
    }

    if (!productLink.trim()) return;

    // Disable button briefly to prevent double clicks
    setIsDisabled(true);
    setTimeout(() => setIsDisabled(false), 3000);

    // Prepare data payload
    const data = {
      name: result?.name || "Waiting",
      link: productLink,
      size,
      color: "", // اللون مش مطلوب
      count: quantity,
      additionalInfo: additionalInformation,
      img: result?.img || "https://api.markaship.com/uploads/waiting.png",
      price: result?.price || 0,
    };

    // ✅ Redux + LocalStorage + فتح الكارت
    const token = localStorage.getItem("accessToken");

    if (token) {
      try {
        await dispatch(createProductItem(data)).unwrap();
        await dispatch(getProductItemById()).unwrap();
        window.dispatchEvent(new Event("openCart"));
      } catch (error) {
        console.error(error);
      }
    } else {
      const uniqueId = `local-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const dataWithId = { ...data, id: uniqueId };

      const existingData = JSON.parse(localStorage.getItem("cartData")) || [];
      existingData.push(dataWithId);
      localStorage.setItem("cartData", JSON.stringify(existingData));
      window.dispatchEvent(new Event("openCart"));
    }

    // ✅ Reset form
    setProductLink("");
    setQuantity(1);
    setAdditionalInformation("");
    setSize("");
    setColor("");

    onClose();
  };

  const handleQuantityChange = (type) => {
    setQuantity((prev) =>
      type === "increment"
        ? prev < 3
          ? prev + 1
          : prev
        : prev > 1
        ? prev - 1
        : prev
    );
  };

  const getBaseUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      return `${parsedUrl.protocol}//${parsedUrl.hostname}/`;
    } catch {
      return url;
    }
  };

  const handleGoToCart = () => {
    const savedCart = JSON.parse(localStorage.getItem("cartData")) || [];
    // console.log("Saved Cart:", savedCart);

    navigateWithLang("/my-account/buy-for-me");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center w-full bg-black bg-opacity-20"
      dir={direction}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-50 flex items-center justify-center"
      >
        <div
          className="relative w-11/12 text-black bg-white rounded-md shadow-lg lg:w-8/12 md:w-10/12 max-h-[80vh] overflow-y-auto"
          ref={ref}
        >
          {/* Header */}
          <div className="relative flex px-4 justify-between py-3 text-black rounded-t-md after:content-[''] after:absolute after:bottom-0 after:left-4 after:right-4 after:h-[1px] after:bg-gray-300">
            <h1 className="my-2 text-sm text-black lg:text-2xl md:text-xl">
              {t("Step2ProductDetails")}
            </h1>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-5 p-3">
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="w-full md:w-1/5">
                <img src={result?.img || Wating} className="w-full" />
              </div>
              <div className="w-full md:w-4/5 text-start">
                {/* name */}
                <div className="py-2 text-lg border-b lg:py-4">
                  <span className="font-light text-black">
                    {t("ProductName")} :
                  </span>
                  <span className="font-bold text-black">
                    {" "}
                    {result?.name || t("waiting")}
                  </span>
                </div>

                {/* link */}
                <div className="py-2 text-lg border-b lg:py-4">
                  <span className="font-light text-black">
                    {t("Website")} :
                  </span>
                  <span className="font-bold text-black">
                    {" "}
                    {getBaseUrl(result?.link)}
                  </span>
                </div>

                {/* Size */}
                <div className="flex flex-col gap-2 py-2 text-lg border-b lg:py-4">
                  <label htmlFor="size" className="font-bold text-black">
                    {t("RequestedSize")}
                  </label>
                  <input
                    id="size"
                    className={`w-full px-2 py-3 text-black bg-white rounded-lg md:px-5 focus:outline-none
      ${
        sizeError
          ? "border border-red-500 ring-2 ring-red-400 focus:ring-red-400"
          : "border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-300"
      }`}
                    type="text"
                    placeholder={t("EnterSize")}
                    value={size}
                    onChange={(e) => {
                      setSize(e.target.value);
                      if (sizeError) setSizeError(false);
                    }}
                  />
                  {sizeError && (
                    <span className="mt-1 text-sm text-red-500">
                      {t("PleaseEnterSize")}
                    </span>
                  )}
                </div>

                {/* Additional info */}
                <div className="flex flex-col gap-2 py-2 text-lg border-b lg:py-4">
                  <label className="font-bold text-black">
                    {t("PleaseEnterAdditionalNotes")}
                  </label>
                  <input
                    id="additionalInfo"
                    className={`w-full px-2 py-3 text-black bg-white rounded-lg md:px-5 focus:outline-none
      ${
        additionalInformationError
          ? "border border-red-500 ring-2 ring-red-400"
          : "border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-300"
      }`}
                    type="text"
                    placeholder={t("AdditionalNotes")}
                    value={additionalInformation}
                    onChange={(e) => {
                      setAdditionalInformation(e.target.value);
                      if (additionalInformationError)
                        setAdditionalInformationError(false);
                    }}
                    onFocus={() => {
                      if (additionalInformationError)
                        setAdditionalInformationError(true);
                    }}
                  />
                  {additionalInformationError && (
                    <span className="mt-1 text-sm text-red-500">
                      {t("PleaseEnterAdditionalNotes")}
                    </span>
                  )}
                </div>
                {/* Color input */}
                {/* <div className="flex flex-col py-2 text-lg border-b lg:py-4">
                  <label className="block mb-2 font-light">
                    {t("Requestedcolor")}
                  </label>
                  <input
                    id="color"
                    className={`w-full px-2 py-3 text-black bg-white rounded-lg md:px-5 focus:outline-none
                    ${
                      colorError
                        ? "border border-red-500 ring-2 ring-red-400"
                        : "border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-300"
                    }`}
                    type="text"
                    placeholder={t("EnterColor")}
                    value={color}
                    onChange={(e) => {
                      setColor(e.target.value);
                      if (colorError) setColorError(false); // clear error on typing
                    }}
                    onFocus={() => {
                      if (colorError) setColorError(true); // optional: keeps error look on focus
                    }}
                  />
                </div> */}

                {/* Quantity */}
                <div className="flex flex-col py-2 text-lg border-b lg:py-4">
                  <label className="block mb-2 font-light">
                    {t("RequestedQuantity")}
                  </label>
                  <div className="flex items-center border w-max">
                    <button
                      className="px-4 py-2 text-black bg-gray-200 hover:bg-gray-300 disabled:bg-gray-300"
                      onClick={() => handleQuantityChange("decrement")}
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <span className="px-4 text-black">{quantity}</span>
                    <button
                      className="px-4 py-2 text-black bg-custom-yellow hover:bg-yellow-500 disabled:bg-gray-300"
                      onClick={() => handleQuantityChange("increment")}
                      disabled={quantity >= 3}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-end gap-4 my-4">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    {t("cancel")}
                  </button>

                  {findCarts.length === 0 ? (
                    <button
                      onClick={handleAddToCart}
                      ref={buttonRef}
                      disabled={isDisabled}
                      className={`inline-flex items-center px-4 py-2 rounded transition ${
                        isDisabled
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-custom-yellow hover:scale-105"
                      }`}
                    >
                      <ShoppingCartIcon className="w-4 text-black me-2" />
                      {t("AddToCart")}
                    </button>
                  ) : (
                    // <button
                    //   onClick={handleGoToCart}
                    //   className="inline-flex items-center px-4 py-2 rounded bg-custom-yellow hover:scale-105"
                    // >
                    //   <ShoppingCartIcon className="w-4 text-black me-2" />
                    //   {t("GoToCart")}
                    // </button>
                    <button
                      onClick={handleAddToCart}
                      ref={buttonRef}
                      disabled={isDisabled}
                      className={`inline-flex items-center px-4 py-2 rounded transition ${
                        isDisabled
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-custom-yellow hover:scale-105"
                      }`}
                    >
                      <ShoppingCartIcon className="w-4 text-black me-2" />
                      {t("AddToCart")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
});

export default GalbElHabebPopup;
